// The three-step publish flow as a pure state machine, so the component only
// wires it to the network: request an upload URL, PUT the file, create the post.

import type { PostMediaKind } from './postValidation';

export type PublishStep =
  | 'idle'
  | 'requestingUrl'
  | 'uploading'
  | 'creating'
  | 'done'
  | 'failed';

export type PublishFailureStep = 'requestingUrl' | 'uploading' | 'creating';

export interface PublishState {
  step: PublishStep;
  progress: number;
  error: string | null;
}

export const initialPublishState: PublishState = {
  step: 'idle',
  progress: 0,
  error: null,
};

export type PublishEvent =
  | { type: 'start' }
  | { type: 'uploadUrlReady' }
  | { type: 'progress'; percent: number }
  | { type: 'uploadComplete' }
  | { type: 'postCreated' }
  | { type: 'fail'; at: PublishFailureStep; message?: string; kind?: PostMediaKind }
  | { type: 'reset' };

function clampPercent(percent: number): number {
  if (!Number.isFinite(percent)) {
    return 0;
  }
  return Math.min(100, Math.max(0, Math.round(percent)));
}

/** Upload progress as a whole percentage; 0 when the total size is unknown. */
export function toPercent(loaded: number, total?: number): number {
  if (typeof total !== 'number' || !Number.isFinite(total) || total <= 0) {
    return 0;
  }
  return clampPercent((loaded / total) * 100);
}

/**
 * What went wrong, and whether the creator ended up with a post. `kind`
 * defaults to 'image' so every existing caller keeps its wording byte for byte;
 * only a video names itself differently.
 */
export function publishFailureMessage(
  at: PublishFailureStep,
  serverMessage?: string,
  kind: PostMediaKind = 'image',
): string {
  const noun = kind === 'video' ? 'video' : 'image';

  let base: string;
  switch (at) {
    case 'requestingUrl':
      // Names no noun, so it is shared.
      base = "Couldn't start the upload. Nothing was posted — please try again.";
      break;
    case 'uploading':
      base = `The ${noun} didn't finish uploading. Nothing was posted — please try again.`;
      break;
    case 'creating':
    default:
      base = `The ${noun} uploaded but the post wasn't created. Please publish again.`;
      break;
  }

  const trimmed = serverMessage?.trim();
  return trimmed ? `${base} (${trimmed})` : base;
}

/**
 * Events that do not fit the current step are ignored (the same state object is
 * returned), so a late progress callback cannot reopen a finished upload.
 */
export function publishReducer(state: PublishState, event: PublishEvent): PublishState {
  switch (event.type) {
    case 'start':
      if (isPublishing(state)) {
        return state;
      }
      return { step: 'requestingUrl', progress: 0, error: null };

    case 'uploadUrlReady':
      if (state.step !== 'requestingUrl') {
        return state;
      }
      return { step: 'uploading', progress: 0, error: null };

    case 'progress': {
      if (state.step !== 'uploading') {
        return state;
      }
      const percent = clampPercent(event.percent);
      if (percent <= state.progress) {
        return state;
      }
      return { ...state, progress: percent };
    }

    case 'uploadComplete':
      if (state.step !== 'uploading') {
        return state;
      }
      return { step: 'creating', progress: 100, error: null };

    case 'postCreated':
      if (state.step !== 'creating') {
        return state;
      }
      return { step: 'done', progress: 100, error: null };

    case 'fail':
      return {
        step: 'failed',
        progress: state.progress,
        error: publishFailureMessage(event.at, event.message, event.kind),
      };

    case 'reset':
      return initialPublishState;

    default:
      return state;
  }
}

export function isPublishing(state: PublishState): boolean {
  return (
    state.step === 'requestingUrl' || state.step === 'uploading' || state.step === 'creating'
  );
}

export function publishStatusLabel(state: PublishState): string {
  switch (state.step) {
    case 'requestingUrl':
      return 'Preparing upload…';
    case 'uploading':
      return `Uploading ${state.progress}%`;
    case 'creating':
      return 'Creating post…';
    case 'done':
      return 'Published';
    default:
      return '';
  }
}

export type UploadUrlParseResult =
  | { ok: true; uploadUrl: string; postId: string; path: string }
  | { ok: false; reason: string };

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Reads exactly the fields the upload-url endpoint returns. Deliberately
 * intolerant: an unexpected shape is a real mismatch, not something to guess at.
 */
export function parseUploadUrlResponse(data: unknown): UploadUrlParseResult {
  const unusable: UploadUrlParseResult = {
    ok: false,
    reason: 'The upload link from the server was unusable.',
  };

  if (!data || typeof data !== 'object') {
    return unusable;
  }

  const { uploadUrl, postId, path } = data as Record<string, unknown>;

  if (!isNonEmptyString(uploadUrl) || !isNonEmptyString(postId) || !isNonEmptyString(path)) {
    return unusable;
  }

  return { ok: true, uploadUrl, postId, path };
}
